import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/react';
import Button from '@atlaskit/button';
import Page from '@atlaskit/page';
import copy from 'copy-to-clipboard';
import { colors, gridSize } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ButtonContainer from './ButtonContainer';
import SelectOnClickText from './SelectOnClickText';
import TallButton from './TallButton';
import RecoveryIcon from '../images/save-recovery-key.svg';
import { media } from '../utilities/style-utils';
import { addFlag } from '../actions/flag-actions';
import messages from './RecoveryPage.messages';
import { Flag } from '../types';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { useAppDispatch } from '../stores/redux.store';

const imageWidth = 359;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
  width: ${gridSize() * 82}px;
  min-height: ${gridSize() * 80}px;
  height: calc(100vh - ${gridSize()}px);
  background: url(${RecoveryIcon}) bottom center/ ${imageWidth}px no-repeat;

  ${media.handheld`
    width: ${gridSize() * 44}px;
    background: none;
    padding-bottom: 0;
  `}
`;

const Subcontainer = styled.div`
  display: flex;
  flex-direction: column;
  width: ${gridSize() * 52}px;

  ${media.handheld`
    width: ${gridSize() * 44}px;
  `}
`;

const Heading = styled.h2`
  display: flex;
  justify-content: center;
  margin-top: ${gridSize() * 5}px !important;

  ${media.handheld`
    display: none;
  `}
`;

const Subheading = styled.h3`
  display: flex;
  align-items: center;
  margin-top: ${gridSize() * 3.5}px !important;

  &::before {
    counter-increment: counter;
    content: counter(counter) '. ';
    margin-right: ${gridSize() * 0.5}px;
  }
`;

const DesktopSubheading = styled(Subheading)`
  ${media.handheld`
    display: none;
  `}
`;

const MobileSubheading = styled(Subheading)`
  display: none;
  justify-content: center;
  font-size: 16px;

  ${media.handheld`
    display: flex;
  `}

  &::before {
    content: '';
    margin: 0;
  }
`;

const RecoveryCode = styled.div`
  display: flex;
  justify-content: space-between;
  font: ${token('font.code')};
  font-size: 24px;
  font-weight: bold;
  color: ${colors.N300};
  margin-top: ${gridSize() * 2}px;

  ${media.handheld`
    display: none;
  `}
`;

const MobileRecoveryCode = styled(SelectOnClickText)`
  display: none;
  text-align: center;
  font: ${token('font.code')};
  font-size: 20px;
  color: ${colors.N300};
  margin-top: ${gridSize() * 2}px;

  ${media.handheld`
    display: block;
  `}
`;

const DropdownMenuContainer = styled.div`
  display: flex;
  justify-content: flex-end;

  ${media.handheld`
    display: none;
  `}
`;

const StyledButton = styled(Button)`
  font-size: 14px !important;
  margin-left: auto !important;
  padding: 0 16px !important;
`;

const MobileButtonContainer = styled(ButtonContainer)`
  display: none;

  ${media.handheld`
    display: block;
  `}
`;

const DesktopParagraph = styled.p`
  ${media.handheld`
    display: none;
  `}
`;

const MobileParagraph = styled.p`
  display: none;

  ${media.handheld`
    display: block;
  `}
`;

type RecoveryPageProps = {
  onComplete: () => void;
  recoveryCode: string;
  isEmbedded: boolean;
};

const RecoveryPage = ({ isEmbedded, recoveryCode, onComplete }: RecoveryPageProps) => {
  const [newCodeSaved, setNewCodeSaved] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    window.onbeforeunload = null;
  }, []);

  const onClickOnRecovery = useCallback(() => {
    const success = copy(recoveryCode);

    if (success) {
      const flag: Flag = {
        type: 'success',
        title: messages.mfRecoveryCopiedTitle,
        description: messages.mfRecoveryCopiedDesc,
      };

      dispatch(addFlag(flag.type, flag.title, flag.description));

      setNewCodeSaved(true);
    }
  }, [dispatch, recoveryCode]);

  const onRecoveryPrint = useCallback(() => {
    // Invocation of this method will freeze the page in React 16 in development mode.
    // It does not affect production mode. The development mode bug is fixed in React 17:
    // https://github.com/facebook/react/issues/16734
    setTimeout(window.print);
    setNewCodeSaved(true);
  }, []);

  const onRecoverySkip = useCallback(() => {
    setNewCodeSaved(true);
  }, []);

  return (
    <Page>
      <Global
        styles={css`
          body {
            counter-reset: counter;
          }
        `}
      />
      <Container>
        <Heading>
          <FormattedMessage
            id="mf.recovery.heading"
            defaultMessage="We created a new emergency recovery key for you"
          />
        </Heading>
        <Subcontainer>
          <DesktopSubheading>
            <FormattedMessage
              id="mf.recovery.savekey"
              defaultMessage="Save this emergency recovery key"
            />
          </DesktopSubheading>
          <MobileSubheading>
            <FormattedMessage id="mf.recovery.savekey.mobile" defaultMessage="Save recovery key" />
          </MobileSubheading>
          <DesktopParagraph>
            <FormattedMessage
              id="mf.recovery.savekey.desc"
              defaultMessage="If you lose access to your phone, you won't be able to log in to your account without this key. Print, copy or write down this key without letting anyone see it."
            />
          </DesktopParagraph>
          <MobileParagraph>
            <FormattedMessage
              id="mf.recovery.savekey.desc.mobile"
              defaultMessage="If you lose access to your phone, you won't be able to log in to your account without the key. Save this key without letting anyone see it."
            />
          </MobileParagraph>
          <RecoveryCode id="recovery-code">
            {(recoveryCode.match(/.{4}/g) || []).map((item, index) => (
              <span key={index}>{item}</span>
            ))}
          </RecoveryCode>
          <input id="recovery-code-assistive" className="assistive" value={recoveryCode} readOnly />
          <MobileRecoveryCode value={(recoveryCode.match(/.{4}/g) || []).join(' ')} />
          <DropdownMenuContainer id="mf-save-options">
            <DropdownMenu
              trigger={({ triggerRef, ...props }) => (
                <Button {...props} ref={triggerRef} iconAfter={<ChevronDownIcon label="" />}>
                  <FormattedMessage id="mf.recovery.saveoptions" defaultMessage="Save options" />
                </Button>
              )}>
              <DropdownItemGroup>
                <DropdownItem onClick={onClickOnRecovery}>
                  <div id="mf-copy-key">
                    <FormattedMessage id="mf.recovery.copy" defaultMessage="Copy to clipboard" />
                  </div>
                </DropdownItem>
                {!isEmbedded && (
                  <DropdownItem onClick={onRecoveryPrint}>
                    <div id="mf-print-key">
                      <FormattedMessage id="mf.recovery.print" defaultMessage="Print" />
                    </div>
                  </DropdownItem>
                )}
                <DropdownItem onClick={onRecoverySkip}>
                  <div id="mf-wrote-key">
                    <FormattedMessage id="mf.recovery.skip" defaultMessage="I wrote it down" />
                  </div>
                </DropdownItem>
              </DropdownItemGroup>
            </DropdownMenu>
          </DropdownMenuContainer>
          <DesktopSubheading>
            <FormattedMessage
              id="mf.recovery.seriously"
              defaultMessage="Seriously, save this key!"
            />
            <StyledButton
              id="two-step-verification-save-recovery-code-continue"
              isDisabled={!newCodeSaved}
              onClick={onComplete}>
              <FormattedMessage id="mf.recovery.saved" defaultMessage="Saved, let's go" />
            </StyledButton>
          </DesktopSubheading>
          <MobileButtonContainer>
            <TallButton appearance="primary" isFullWidth onClick={onComplete}>
              <FormattedMessage id="mf.recovery.saved" defaultMessage="Saved, let's go" />
            </TallButton>
          </MobileButtonContainer>
        </Subcontainer>
      </Container>
    </Page>
  );
};

export default RecoveryPage;
